import React, { useState, useRef, useEffect } from 'react';
import { useApi } from '../../contexts/ApiContext';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatTime } from '../../utils/dateFormatter';
import './AiAssistant.css';

interface ExecutedAction {
  type: string;
  result: {
    success: boolean;
    message?: string;
    orden?: any;
    pedido?: any;
    backup?: any;
    [key: string]: any;
  };
  timestamp: string;
}

interface Message {
  role: 'user' | 'model';
  content: string;
  type?: 'text' | 'chart' | 'table' | 'sql' | 'action';
  data?: any;
  sqlQuery?: string;
  sqlResults?: {
    query: string;
    execution_time_ms: number;
    row_count: number;
    columns: Array<{ name: string; type: number }>;
    data: any[];
  };
  executedActions?: ExecutedAction[];
  timestamp: Date;
}

export default function AiAssistant() {
  const [messages, setMessages] = useState<Message[]>([
    { 
      role: 'model', 
      content: 'Hola, soy tu asistente avanzado. Puedo analizar datos, generar gráficos, leer imágenes y ayudarte con la gestión total del inventario. ¿Qué necesitas hoy?',
      type: 'text',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { api } = useApi();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inputText.trim() && !fileInputRef.current?.files?.length) return;

    const userMessage: Message = {
      role: 'user',
      content: inputText,
      type: 'text',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // Preparar historial para enviar
      const history = messages.slice(-20).map(m => ({
        role: m.role,
        content: m.content
      }));

      const files = fileInputRef.current?.files;
      const formData = new FormData();
      formData.append('message', userMessage.content);
      formData.append('history', JSON.stringify(history));
      
      if (files && files.length > 0) {
        Array.from(files).forEach(file => {
          formData.append('files', file);
        });
      }
      
      // Limpiar input de archivos después de preparar formData
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      const response = await api.post('/ai/chat', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Detectar si la respuesta incluye datos estructurados para gráficos o SQL
      const responseData = response.data;
      let modelMessage: Message = {
        role: 'model',
        content: responseData.text,
        type: 'text',
        timestamp: new Date()
      };

      // Si el backend devuelve datos estructurados para gráficos
      if (responseData.chartData) {
        modelMessage.type = 'chart';
        modelMessage.data = responseData.chartData;
      }

      // Detectar si la respuesta contiene resultados SQL
      if (responseData.sqlResults) {
        modelMessage.type = 'sql';
        modelMessage.sqlQuery = responseData.sqlResults.query;
        modelMessage.sqlResults = responseData.sqlResults;
      }

      // Detectar acciones ejecutadas
      if (responseData.executedActions && responseData.executedActions.length > 0) {
        modelMessage.executedActions = responseData.executedActions;
        if (!modelMessage.type) {
          modelMessage.type = 'action';
        }
      }

      setMessages(prev => [...prev, modelMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'model', 
        content: 'Lo siento, hubo un error al procesar tu solicitud.', 
        type: 'text', 
        timestamp: new Date() 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const getActionIcon = (actionType: string): string => {
    const icons: Record<string, string> = {
      'createWorkOrder': '📋',
      'createPurchaseOrderFundicion': '🏭',
      'createPurchaseOrderPiedras': '💎',
      'createCoatingOrder': '✨',
      'adjustStock': '📦',
      'transferStock': '🔄',
      'setMinStock': '⚙️',
      'createMetalPiece': '🔧',
      'createStone': '💠',
      'completeWorkOrderLine': '✅',
      'receiveFundicionOrder': '📥',
      'receivePiedrasOrder': '📥',
      'receiveCoatingOrder': '📥',
      'syncShopify': '🛒',
      'importSales': '📊',
      'createBackup': '💾'
    };
    return icons[actionType] || '⚡';
  };

  const getActionLabel = (actionType: string): string => {
    const labels: Record<string, string> = {
      'createWorkOrder': 'Orden de Fabricación',
      'createPurchaseOrderFundicion': 'Pedido de Fundición',
      'createPurchaseOrderPiedras': 'Pedido de Piedras',
      'createCoatingOrder': 'Orden de Revestimiento',
      'adjustStock': 'Ajuste de Stock',
      'transferStock': 'Transferencia de Stock',
      'setMinStock': 'Configuración de Stock Mínimo',
      'createMetalPiece': 'Pieza Metálica',
      'createStone': 'Piedra',
      'completeWorkOrderLine': 'Línea Completada',
      'receiveFundicionOrder': 'Recepción de Fundición',
      'receivePiedrasOrder': 'Recepción de Piedras',
      'receiveCoatingOrder': 'Recepción de Revestimiento',
      'syncShopify': 'Sincronización Shopify',
      'importSales': 'Importación de Ventas',
      'createBackup': 'Backup'
    };
    return labels[actionType] || actionType;
  };

  const renderMessageContent = (msg: Message) => {
    // Mostrar acciones ejecutadas primero si existen
    if (msg.executedActions && msg.executedActions.length > 0) {
      return (
        <div className="message-actions-container">
          {msg.executedActions.map((action, idx) => (
            <div key={idx} className="executed-action-card">
              <div className="action-header">
                <span className="action-icon">{getActionIcon(action.type)}</span>
                <span className="action-title">{getActionLabel(action.type)}</span>
                <span className="action-badge success">✓ Ejecutado</span>
              </div>
              {action.result.message && (
                <div className="action-message">{action.result.message}</div>
              )}
              {action.result.orden && (
                <div className="action-details">
                  <div><strong>Código:</strong> {action.result.orden.codigo || action.result.orden.code}</div>
                  <div><strong>Estado:</strong> {action.result.orden.estado || action.result.orden.status}</div>
                </div>
              )}
              {action.result.pedido && (
                <div className="action-details">
                  <div><strong>Código:</strong> {action.result.pedido.codigo || action.result.pedido.code}</div>
                  <div><strong>Estado:</strong> {action.result.pedido.estado || action.result.pedido.status}</div>
                </div>
              )}
              {action.result.backup && (
                <div className="action-details">
                  <div><strong>ID:</strong> {action.result.backup.id}</div>
                  <div><strong>Fecha:</strong> {new Date(action.result.backup.fecha || action.result.backup.createdAt).toLocaleString()}</div>
                </div>
              )}
            </div>
          ))}
          {msg.content && (
            <div className="markdown-body" style={{ marginTop: '1rem' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {msg.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      );
    }

    if (msg.type === 'chart' && msg.data) {
      return (
        <div className="message-chart-container">
          <div className="markdown-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {msg.content}
            </ReactMarkdown>
          </div>
          <div style={{ width: '100%', height: 300, marginTop: '1rem' }}>
            <ResponsiveContainer>
              <BarChart data={msg.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#008060" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    if (msg.type === 'sql' && msg.sqlResults) {
      return (
        <div className="message-sql-container">
          <div className="markdown-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {msg.content}
            </ReactMarkdown>
          </div>
          <div className="sql-query-box">
            <div className="sql-query-header">
              <span className="sql-icon">💾</span>
              <span className="sql-label">Consulta SQL</span>
              <span className="sql-meta">
                {msg.sqlResults.execution_time_ms}ms • {msg.sqlResults.row_count} filas
              </span>
            </div>
            <pre className="sql-query-code">{msg.sqlQuery || msg.sqlResults.query}</pre>
          </div>
          {msg.sqlResults.data && msg.sqlResults.data.length > 0 && (
            <div className="sql-results-table">
              <table>
                <thead>
                  <tr>
                    {msg.sqlResults.columns.map((col, idx) => (
                      <th key={idx}>{col.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {msg.sqlResults.data.slice(0, 100).map((row: any, rowIdx: number) => (
                    <tr key={rowIdx}>
                      {msg.sqlResults!.columns.map((col, colIdx) => (
                        <td key={colIdx}>
                          {row[col.name] !== null && row[col.name] !== undefined
                            ? String(row[col.name])
                            : 'NULL'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {msg.sqlResults.data.length > 100 && (
                <div className="sql-results-footer">
                  Mostrando 100 de {msg.sqlResults.data.length} filas
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="message-text">
        <div className="markdown-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {msg.content}
          </ReactMarkdown>
        </div>
      </div>
    );
  };

  return (
    <div className="ai-assistant-page">
      <div className="ai-sidebar">
        <h3>Historial</h3>
        <div className="history-list">
          <div className="history-item active">Sesión Actual</div>
          {/* Aquí irían sesiones guardadas */}
        </div>
      </div>
      
      <div className="ai-main">
        <div className="ai-header">
          <h2>Asistente Inteligente</h2>
          <div className="ai-capabilities">
            <span title="Análisis de datos">📊</span>
            <span title="Lectura de imágenes">👁️</span>
            <span title="Gestión de archivos">📁</span>
          </div>
        </div>

        <div className="ai-messages">
          {messages.map((msg, idx) => (
            <div key={idx} className={`ai-message ${msg.role}`}>
              <div className="message-avatar">
                {msg.role === 'user' ? '👤' : '✨'}
              </div>
              <div className="message-bubble">
                {renderMessageContent(msg)}
                <div className="message-time">
                  {formatTime(msg.timestamp)}
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="ai-message model">
              <div className="message-avatar">✨</div>
              <div className="message-bubble loading">
                <span className="dot">.</span><span className="dot">.</span><span className="dot">.</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="ai-input-area">
          <form onSubmit={handleSendMessage} className="ai-input-form">
            <button 
              type="button" 
              className="ai-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              title="Adjuntar imagen o archivo"
            >
              📎
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              accept="image/*,.csv,.pdf"
              multiple
            />
            <textarea
              className="ai-textarea"
              placeholder="Escribe tu consulta aquí..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <button type="submit" className="ai-send-btn" disabled={isLoading || (!inputText.trim() && !fileInputRef.current?.files?.length)}>
              ➤
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
